/**
 * Event date/time formatting for cards and rails.
 *
 * Two bugs this exists to fix, both previously inlined in EventCard:
 *
 * 1. Date-only values shifted a day. `new Date('2026-08-25')` is parsed as
 *    UTC midnight, so a Pacific viewer saw "Aug 24 · 5:00 PM" — wrong day, and
 *    a showtime that was never in the data. 9 upcoming events have a null
 *    event_datetime and hit this path.
 *
 * 2. Times rendered in the viewer's zone, not the venue's. A 7:00 PM show at
 *    Blue Note read "10:00 PM" to anyone on Eastern time. Showtimes are local
 *    to the venue and should read the same wherever the phone is.
 */

/**
 * Every event currently in the table is America/Los_Angeles, and the daily pull
 * is geo-scoped to 50mi around DTLA. This is the fallback, not an assumption
 * baked in: `event.timezone` wins whenever it is present.
 *
 * The feed RPCs (get_popular_events / get_events_by_date) do not select the
 * timezone column yet, so today it is always the fallback that applies. Add
 * `e.timezone` to their RETURNS TABLE and it starts flowing through on its own
 * — worth doing before Targeted Event Pull is used for a venue outside Pacific
 * time, e.g. Red Rocks.
 */
export const DEFAULT_EVENT_TIMEZONE = 'America/Los_Angeles';

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function resolveTimeZone(event) {
  const tz = event?.timezone;
  return typeof tz === 'string' && tz.trim() ? tz.trim() : DEFAULT_EVENT_TIMEZONE;
}

/**
 * Render a bare 'YYYY-MM-DD' using its own digits.
 *
 * Formatting it in a real zone is what causes the day shift, so the parts are
 * pinned into UTC and read back in UTC — the calendar date survives untouched.
 */
function formatDateOnly(value) {
  const [year, month, day] = value.split('-').map(Number);
  const utc = new Date(Date.UTC(year, month - 1, day));
  if (isNaN(utc.getTime())) return null;
  return utc.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** Pick the best available instant for an event, or null. */
function resolveEventInstant(event) {
  return event?.event_datetime || event?.event_date || null;
}

/**
 * "Aug 25" — the date alone, in the venue's zone.
 */
export function formatEventDate(event) {
  const value = resolveEventInstant(event);
  if (!value) return 'Date TBA';

  if (typeof value === 'string' && DATE_ONLY_PATTERN.test(value)) {
    return formatDateOnly(value) ?? 'Date TBA';
  }

  const date = new Date(value);
  if (isNaN(date.getTime())) return 'Date TBA';

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: resolveTimeZone(event),
  });
}

/**
 * "Aug 25 · 7:00 PM" — or just "Aug 25" when the event carries no showtime.
 *
 * Returning the bare date for a date-only event is deliberate: the old code
 * invented "5:00 PM" out of a UTC-midnight parse, and a made-up doors time is
 * worse than none.
 */
export function formatEventDateTime(event) {
  const value = resolveEventInstant(event);
  if (!value) return 'Date TBA';

  if (typeof value === 'string' && DATE_ONLY_PATTERN.test(value)) {
    return formatDateOnly(value) ?? 'Date TBA';
  }

  const date = new Date(value);
  if (isNaN(date.getTime())) return 'Date TBA';

  const timeZone = resolveTimeZone(event);
  const monthDay = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone,
  });
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
  });

  return `${monthDay} · ${time}`;
}

/**
 * "Monday, August 25, 2026" — the long form used on the event detail screen.
 *
 * Returns '' rather than 'Date TBA' for a missing value: the detail screen
 * renders its own 'TBA' placeholder in that slot.
 */
export function formatEventDateLong(event) {
  const value = resolveEventInstant(event);
  if (!value) return '';

  if (typeof value === 'string' && DATE_ONLY_PATTERN.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    const utc = new Date(Date.UTC(year, month - 1, day));
    if (isNaN(utc.getTime())) return '';
    return utc.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }

  const date = new Date(value);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: resolveTimeZone(event),
  });
}

/**
 * "7:00 PM" — the showtime alone, in the venue's zone.
 *
 * A date-only event has no showtime to render, so this returns '' rather than
 * inventing one from a UTC-midnight parse.
 */
export function formatEventTime(event) {
  const value = resolveEventInstant(event);
  if (!value) return '';
  if (typeof value === 'string' && DATE_ONLY_PATTERN.test(value)) return '';

  const date = new Date(value);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: resolveTimeZone(event),
  });
}

const DAY_MS = 24 * 3600 * 1000;

/**
 * "4:30 PM" for a moment later today, "Sat 4:30 PM" for one in the next six days, null beyond - in the venue's zone,
 * like a showtime. For Find's "turns on at" before the window opens (Sam's pick C, 2026-11-01).
 */
export function formatEventMomentSoon(event, ms, now = Date.now()) {
  if (!Number.isFinite(ms) || ms < now || ms - now >= 6 * DAY_MS) return null;
  const timeZone = resolveTimeZone(event);
  const day = (t) => new Date(t).toLocaleDateString('en-CA', { timeZone });
  const time = new Date(ms).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
  });
  if (day(ms) === day(now)) return time;
  return `${new Date(ms).toLocaleDateString('en-US', { weekday: 'short', timeZone })} ${time}`;
}

/** "Nov 14" - the day of a moment, in the venue's zone; '' when there is none. Find's "turns on" past six days. */
export function formatEventMomentDay(event, ms) {
  if (!Number.isFinite(ms)) return '';
  return new Date(ms).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: resolveTimeZone(event),
  });
}
